import {
    ArrayModel,
    assignModelOptions,
    ClientManagerInstanceToken,
    CompositeModel,
    ConfigConstants,
    ConfigService,
    CustomInjector,
    MaterialCheckModel,
    MaterialService,
    ModelOptionsLoaderService,
    NotificationModel,
    NumberModel,
    TextModel,
    UserMasterService,
} from '@evorait/pace-core';
import * as _ from 'lodash';

export class MaterialPricingModel extends CompositeModel {
    public materialNumber: TextModel;
    public quantity: NumberModel;
    public materials: TextModel;
    public partSet: ArrayModel<MaterialCheckModel>;
    public frontId: TextModel;
    public notificationNo: TextModel;
    public salesOrg: TextModel;
    public plant: TextModel;
    public longText: ArrayModel<TextModel>;
    public isMaterialRequested = false;
    public sparePartsCatalogLink: string;
    public constructor(
        public notificationModel: NotificationModel,
        materialOrderList?: ArrayModel<MaterialCheckModel>,
    ) {
        super('MaterialPricingModel');

        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigService);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const userMasterService = injector.get(UserMasterService);
        const clientManager = injector.get(ClientManagerInstanceToken);
        const materialService = injector.get(MaterialService);

        this.sparePartsCatalogLink = configService.getSparePartsCatalogLink();

        this.materialNumber = new TextModel('MaterialNumber');
        modelOptionsLoader.load(
            this.materialNumber,
            'MaterialPricing',
            'MaterialNumber',
            notificationModel.processIndicator,
        );
        this.materialNumber = assignModelOptions(
            this.materialNumber,
            {
                ...this.materialNumber.options,
                applicationIdentifier: ConfigConstants.ApplicationIdentifier.ProductIdentifier,
            },
            this,
        );

        this.quantity = new NumberModel('Quantity', 1);
        modelOptionsLoader.load(this.quantity, 'MaterialPricing', 'Quantity');

        this.partSet = <ArrayModel<MaterialCheckModel>>this.set(new ArrayModel<MaterialCheckModel>('PartSet', []));
        if (clientManager.isNgmP58()) {
            this.longText = <ArrayModel<TextModel>>this.set(new ArrayModel('LtextSet', []));
        }
        // if material pricing gets called from material order screen,
        // the already added part set in order screen should be shown here
        if (materialOrderList) {
            this.partSet.addModels(materialOrderList.value);
            if (this.longText !== undefined) {
                materialOrderList.value.forEach((material) =>
                    this.longText.addModel(new TextModel('Material', material.materialNr.value)),
                );
            }
        }

        this.frontId = <TextModel>this.set(new TextModel('FrontId', configService.getUserFrontId()));

        this.notificationNo = <TextModel>this.set(new TextModel('NotificationNo', notificationModel.identifier.qmNum));

        // For NgmAA salesOrg and Plant needs to be in the POST, Additionally quantity should always be 1.
        if (clientManager.isNgmAP0()) {
            userMasterService.setSalesOrgAndPlantForModel(this);
        }
        this.isMaterialRequested = materialService.isMaterialRequested(this.notificationModel.identifier);
    }

    /**
     * This function adds the current material and its quantity to the PartSet model.
     */
    public addMaterialToPartSet(): void {
        const currentMaterial = new MaterialCheckModel(this.materialNumber.value, this.quantity.value ?? 0, '');

        this.partSet.addModel(currentMaterial);
        if (this.longText !== undefined) {
            this.longText.addModel(new TextModel('Material', this.materialNumber.value));
        }
        this.resetMaterialInput();
    }

    public resetMaterialInput(): void {
        const materialNumber = _.cloneDeep(this.materialNumber);
        materialNumber.value = '';
        this.materialNumber = materialNumber;

        const quantity = _.cloneDeep(this.quantity);
        quantity.value = 1;
        this.quantity = quantity;
    }

    /**
     * This function delete the  material completely from the PartSet model.
     *
     * @param index index of the material to be removed
     */
    public deleteMaterialFromPartSet(index: number): void {
        this.partSet.removeModel(index);
        if (this.longText !== undefined) {
            this.longText.removeModel(index);
        }
    }
}
